import { Link } from '@tanstack/react-router';
import { CodeBlock } from '../../components';

export default function Content() {
  return (
    <>
    <p><strong>Draco</strong> is Google's open-source library for compressing 3D geometry. In GLB and glTF files it is applied through the <code>KHR_draco_mesh_compression</code> extension and typically shrinks mesh data by 80–95%, so geometry-heavy models download much faster. Below: how Draco works, how much it saves on a whole GLB, when Meshopt is the better choice, and how to apply and load Draco-compressed files.</p>

    <h2>Understanding Draco: Beyond the Basics</h2>

    <p>At its core, Draco is engineered to compress 3D graphics data, specifically targeting:</p>

    <ul>
      <li>Vertex positions</li>
      <li>Normals</li>
      <li>Colors</li>
      <li>Texture coordinates</li>
      <li>Generic attributes associated with geometry</li>
    </ul>

    <p>By efficiently encoding this data, Draco achieves impressive compression ratios, often reducing file sizes by up to 95% without significant loss of visual fidelity. This efficiency is crucial for applications where bandwidth and storage are at a premium.</p>

    <h2>The Mechanics of Draco Compression</h2>

    <p>Draco's compression prowess stems from a combination of sophisticated techniques:</p>

    <h3>1. Geometry Quantization</h3>

    <p>Quantization involves reducing the precision of vertex attributes. For instance, vertex positions might be quantized to 14 bits per component. This process balances the trade-off between compression efficiency and the preservation of visual detail.</p>

    <h3>2. Connectivity Compression</h3>

    <p>Draco analyzes the mesh's topology to identify patterns and redundancies in the connectivity data—how vertices form faces. By exploiting these patterns, it encodes the connectivity information more compactly.</p>

    <h3>3. Entropy Coding</h3>

    <p>After quantization and connectivity compression, Draco applies entropy coding techniques, such as arithmetic coding, to further reduce data size by encoding frequently occurring patterns with shorter codes.</p>

    <h2>How Much Does Draco Compression Reduce File Size?</h2>

    <p>Draco only compresses <strong>geometry</strong> (vertex positions, normals, UVs, colors and triangle indices). Geometry data itself typically shrinks by roughly 80–95% at default quantization settings, but how much the whole GLB file shrinks depends on how much of the file is geometry:</p>

    <table>
      <thead>
        <tr><th>Type of model</th><th>What makes the file big</th><th>Typical effect of Draco on the whole file</th></tr>
      </thead>
      <tbody>
        <tr><td>CAD, scans, photogrammetry meshes, high-poly sculpts</td><td>Geometry</td><td>Large: often 70–90% smaller</td></tr>
        <tr><td>Characters and game assets with 1–2K textures</td><td>Mixed</td><td>Moderate: often 30–60% smaller</td></tr>
        <tr><td>Product models with 4K PBR textures</td><td>Textures</td><td>Small: textures are untouched, so resize and convert them to WebP/AVIF as well</td></tr>
      </tbody>
    </table>

    <p>That is why a good GLB compressor combines Draco with texture resizing and re-encoding. To check how geometry-heavy your model is, run it through the <Link to="/gltf-validator">glTF validator</Link>, which reports vertex and triangle counts and the extensions already in use.</p>

    <h2>Draco vs Meshopt</h2>

    <p>Both are official glTF geometry compression extensions:</p>

    <ul>
      <li><strong>Draco</strong> (<code>KHR_draco_mesh_compression</code>) usually produces the smallest files for static meshes, at the cost of slower decoding and a WebAssembly decoder (around 300 KB) that your viewer has to load.</li>
      <li><strong>Meshopt</strong> (<code>EXT_meshopt_compression</code>) decodes much faster, also compresses animation and morph target data, and works well on top of gzip or Brotli, but files are usually somewhat larger than with Draco.</li>
    </ul>

    <p>Pick Draco when download size matters most (most websites, AR and e-commerce viewers). Consider Meshopt for heavily animated scenes or when decoding speed on low-end devices matters more than bytes. The full comparison, including engine support and gzip behavior, is in <Link to="/blog/draco-vs-meshopt">Draco vs Meshopt</Link>.</p>

    <h2>Draco and glTF: A Synergistic Integration</h2>

    <p>Draco seamlessly integrates with the <a href="https://www.khronos.org/gltf/" target="_blank" rel="noopener noreferrer">glTF</a> (GL Transmission Format) ecosystem through the <code>KHR_draco_mesh_compression</code> extension. This integration allows developers to include compressed geometry within glTF files, facilitating efficient transmission and rendering in web applications.</p>

    <p>Popular 3D engines and libraries, such as <a href="https://threejs.org/" target="_blank" rel="noopener noreferrer">Three.js</a> and <a href="https://www.babylonjs.com/" target="_blank" rel="noopener noreferrer">Babylon.js</a>, support Draco-compressed glTF files, enabling widespread adoption across various platforms.</p>

    <h2>Implementing Draco Compression: A Practical Guide</h2>

    <p>To harness the benefits of Draco compression, follow these general steps:</p>

    <ol>
      <li>Install the Draco encoder from the <a href="https://github.com/google/draco" target="_blank" rel="noopener noreferrer">official GitHub repository</a>.</li>
      <li>Use the encoder to compress your mesh or point cloud data:</li>
    </ol>

    <CodeBlock language="bash">
      {`./draco_encoder -i input.obj -o output.drc`}
    </CodeBlock>

    <p>To decompress and view the model:</p>

    <CodeBlock language="bash">
      {`./draco_decoder -i output.drc -o decompressed.obj`}
    </CodeBlock>

    <p>For glTF and GLB files you don't need the raw encoder. The <code>gltf-transform</code> CLI applies Draco directly to a GLB:</p>

    <CodeBlock language="bash">
      {`npx @gltf-transform/cli draco input.glb output.glb`}
    </CodeBlock>

    <h3>Loading Draco-compressed GLB files in three.js</h3>

    <p>Your viewer needs a Draco decoder. In three.js, attach a <code>DRACOLoader</code> to the <code>GLTFLoader</code>:</p>

    <CodeBlock language="javascript">
      {`import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';

const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.7/');

const loader = new GLTFLoader();
loader.setDRACOLoader(dracoLoader);
loader.load('model.glb', (gltf) => scene.add(gltf.scene));`}
    </CodeBlock>

    <p>Babylon.js and <code>&lt;model-viewer&gt;</code> decode Draco out of the box. In Unity, glTFast decodes Draco once you add the separate Draco for Unity package. For the full loader setup, including Meshopt and KTX2 decoders and common errors, see <Link to="/blog/threejs-load-glb">how to load GLB models in three.js</Link>.</p>

    <h2>Draco Compression Without the Command Line: Online Solutions</h2>

    <p>
      For developers and artists who prefer a more accessible approach, several
      web-based tools offer Draco compression through simple browser
      interfaces. These online solutions eliminate the need for local
      installations and command-line operations, making 3D optimization
      accessible to everyone.
    </p>

    <p>
      Our <Link to="/">GLB compressor</Link> applies Draco together with texture
      resizing, WebP/AVIF conversion, deduplication and pruning in one pass.
      Upload a .glb or .gltf file, pick a preset or tune the Draco
      compression level and quantization bits, and download the result. Batch
      uploads and a <Link to="/docs/api">REST API</Link> are available for
      pipelines.
    </p>


    <h2>Performance Benefits and Considerations</h2>

    <p>Implementing Draco compression offers several advantages:</p>

    <ul>
      <li><strong>Reduced File Sizes:</strong> Significantly smaller 3D asset files lead to faster download times and lower bandwidth usage.</li>
      <li><strong>Faster Load Times:</strong> Smaller files mean quicker parsing and rendering, enhancing user experience.</li>
      <li><strong>Lower Storage Requirements:</strong> Compressed assets consume less disk space, beneficial for both developers and end-users.</li>
    </ul>

    <p>However, it's essential to be aware of potential limitations:</p>

    <ul>
      <li><strong>Decompression Overhead:</strong> Decompressing Draco files requires computational resources, which might impact performance on low-powered devices.</li>
      <li><strong>Lossy Compression:</strong> Quantization introduces some loss of precision. While often imperceptible, it's crucial to balance compression levels with visual fidelity requirements.</li>
      <li><strong>Compatibility:</strong> Ensure that your target platform or engine supports Draco decoding, especially when targeting a broad audience.</li>
    </ul>

    <h2>Conclusion</h2>

    <p>Draco compression stands as a powerful tool in the realm of 3D graphics, enabling developers to optimize assets for performance without sacrificing quality. By understanding its mechanisms and integrating it thoughtfully into your workflow, you can deliver rich 3D experiences that are both efficient and visually compelling.</p>

    <p>For more information and to access the Draco library, visit the <a href="https://google.github.io/draco/" target="_blank" rel="noopener noreferrer">official Draco website</a>.</p>
    </>
  );
}
